import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { MensagemRetida } from '@central/contracts';
import { CheckCircle2, Inbox, MailWarning, Search, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { Card, CardBody } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonText } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDataHora, formatarDistancia } from '@/lib/format';
import { Modal } from '@/features/tickets/Modal';
import {
  chavesDeRetidas,
  listarRetidas,
  obterRetida,
  resumoDasRetidas,
} from '@/features/email/email.api';
import { PainelDeDecisao } from '@/features/email/PainelDeDecisao';
import { RESUMO_DAS_RETIDAS, ROTULO_DAS_RETIDAS } from '@/features/email/navegacao';
import { rotularAutenticacao, type RotuloDeAutenticacao } from '@/features/email/retidas';

/**
 * A fila de mensagens retidas.
 *
 * O e-mail chegou, foi autenticado, foi correlacionado como chamado novo — e
 * ficou retido porque o remetente não pertence a nenhuma organização
 * cadastrada. A retenção está certa; o que faltava era um humano conseguir
 * **ver** que ela existe. Sem esta tela, retenção é indistinguível de mensagem
 * perdida, e o cliente do outro lado acha que abriu chamado.
 *
 * ## Por que a linha abre uma janela, e não uma página
 *
 * Porque a decisão precisa do **conteúdo** — quem tria não decide por
 * remetente e assunto —, e porque a fila é para ser drenada em sequência.
 * Navegar para outra página e voltar a cada mensagem perderia a posição na
 * lista a cada decisão.
 */
export function MensagensRetidasPage() {
  const [busca, setBusca] = useState('');
  const [abertaId, setAbertaId] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const filtro = busca.trim() ? { q: busca.trim() } : {};
  const consulta = useQuery({
    queryKey: chavesDeRetidas.lista(filtro),
    queryFn: () => listarRetidas(filtro),
  });

  const mensagens = consulta.data?.items ?? [];

  /**
   * O total verdadeiro, para a lista não mentir por omissão.
   *
   * A listagem tem teto de 100. Sem isto, uma fila de 340 mostraria 100 e o
   * emblema diria 340, sem nada explicando as 240 que faltam — e o operador
   * concluiria que a tela está quebrada, ou pior, que já limpou o que havia.
   */
  const resumo = useQuery({ queryKey: chavesDeRetidas.resumo, queryFn: resumoDasRetidas });
  const total = resumo.data?.retidas ?? mensagens.length;
  const truncada = !busca && total > mensagens.length;

  return (
    <div className="animate-slide-up space-y-6">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <MailWarning className="h-5 w-5 text-warning" aria-hidden />
          <h1 className="text-2xl font-semibold tracking-tight">{ROTULO_DAS_RETIDAS}</h1>
          {total > 0 ? (
            <span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-semibold text-warning">
              {total}
            </span>
          ) : null}
        </div>
        <p className="max-w-3xl text-sm text-muted-foreground">{RESUMO_DAS_RETIDAS}</p>
      </header>

      {aviso ? (
        <div
          role="status"
          className="flex items-start gap-2 rounded-lg border border-success/30 bg-success/5 px-4 py-3 text-sm"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-success" aria-hidden />
          <span>{aviso}</span>
        </div>
      ) : null}

      <div className="relative max-w-sm">
        <Search
          className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
          aria-hidden
        />
        <input
          aria-label="Buscar por remetente ou assunto"
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
          placeholder="Remetente ou assunto"
          className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:shadow-glow focus-visible:outline-none"
        />
      </div>

      <Card>
        <CardBody className="p-0">
          {consulta.isPending ? (
            <div className="p-5">
              <SkeletonText lines={4} />
            </div>
          ) : consulta.isError ? (
            <div className="p-5">
              <ErrorState
                error={consulta.error}
                onRetry={() => void consulta.refetch()}
                title="A fila de mensagens retidas não carregou"
              />
            </div>
          ) : mensagens.length === 0 ? (
            <div className="p-5">
              <EmptyState
                icon={<Inbox className="h-7 w-7" aria-hidden />}
                title={busca ? 'Nenhuma mensagem casa com a busca' : 'Nenhuma mensagem retida'}
                description={
                  busca
                    ? 'Tente outro trecho do remetente ou do assunto.'
                    : 'Tudo o que chegou à caixa virou chamado ou foi descartado com motivo registrado.'
                }
              />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {mensagens.map((mensagem) => (
                <LinhaDaFila
                  key={mensagem.id}
                  mensagem={mensagem}
                  aoAbrir={() => {
                    setAviso(null);
                    setAbertaId(mensagem.id);
                  }}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {truncada ? (
        <p className="text-sm text-muted-foreground">
          Mostrando as {mensagens.length} mais antigas de {total}. Decida estas e as
          seguintes aparecem — a fila é drenada da mais velha para a mais nova.
        </p>
      ) : null}

      <JanelaDaMensagem
        id={abertaId}
        aoFechar={() => setAbertaId(null)}
        aoDecidir={(texto) => {
          setAbertaId(null);
          setAviso(texto);
        }}
      />
    </div>
  );
}

// ── a lista ────────────────────────────────────────────────────────────────

function LinhaDaFila({ mensagem, aoAbrir }: { mensagem: MensagemRetida; aoAbrir: () => void }) {
  const autenticacao = rotularAutenticacao(mensagem.autenticacao);

  return (
    <li>
      <button
        type="button"
        onClick={aoAbrir}
        className="flex w-full flex-col gap-1 px-5 py-3 text-left transition-colors hover:bg-accent focus-visible:shadow-glow focus-visible:outline-none"
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-medium">{mensagem.nomeDoRemetente ?? mensagem.remetente}</span>
          <span className="font-mono text-xs text-muted-foreground">{mensagem.remetente}</span>
          <SeloDeAutenticacao rotulo={autenticacao} />
          {mensagem.emLiberacao ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              Em liberação por outra pessoa
            </span>
          ) : null}
          {mensagem.outrasDoMesmoRemetente > 0 ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              +{mensagem.outrasDoMesmoRemetente} do mesmo endereço
            </span>
          ) : null}
        </div>

        <span className="text-sm">{mensagem.assunto ?? '(sem assunto)'}</span>

        {mensagem.resumo ? (
          <span className="line-clamp-2 text-xs text-muted-foreground">{mensagem.resumo}</span>
        ) : null}

        <span className="text-xs text-muted-foreground">
          {/* Distância e data absoluta juntas: "há 6 dias" é o que dói, mas é a
              data que a pessoa cola no e-mail de desculpas ao cliente. */}
          Chegou {formatarDistancia(mensagem.recebidaEm)} · {formatarDataHora(mensagem.recebidaEm)}
        </span>
      </button>
    </li>
  );
}

const TOM: Record<RotuloDeAutenticacao['tom'], { classe: string; icone: typeof ShieldCheck }> = {
  ok: { classe: 'bg-success/10 text-success', icone: ShieldCheck },
  alerta: { classe: 'bg-destructive/10 text-destructive', icone: ShieldAlert },
  neutro: { classe: 'bg-muted text-muted-foreground', icone: ShieldQuestion },
};

function SeloDeAutenticacao({ rotulo }: { rotulo: RotuloDeAutenticacao }) {
  const { classe, icone: Icone } = TOM[rotulo.tom];
  return (
    <span
      title={rotulo.detalhe}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        classe,
      )}
    >
      <Icone className="h-3 w-3" aria-hidden />
      {rotulo.texto}
    </span>
  );
}

// ── a janela ───────────────────────────────────────────────────────────────

function JanelaDaMensagem({
  id,
  aoFechar,
  aoDecidir,
}: {
  id: string | null;
  aoFechar: () => void;
  aoDecidir: (texto: string) => void;
}) {
  const consulta = useQuery({
    queryKey: chavesDeRetidas.detalhe(id ?? ''),
    queryFn: () => obterRetida(id!),
    enabled: id !== null,
  });

  const mensagem = consulta.data;
  const autenticacao = mensagem ? rotularAutenticacao(mensagem.autenticacao) : null;

  return (
    <Modal
      aberto={id !== null}
      aoFechar={aoFechar}
      titulo={mensagem?.assunto ?? 'Mensagem retida'}
      descricao={mensagem ? `De ${mensagem.remetente}` : undefined}
    >
      {consulta.isPending && id !== null ? (
        <SkeletonText lines={6} />
      ) : consulta.isError ? (
        <ErrorState error={consulta.error} onRetry={() => void consulta.refetch()} />
      ) : mensagem && autenticacao ? (
        <div className="space-y-4">
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Dado rotulo="Chegou">{formatarDataHora(mensagem.recebidaEm)}</Dado>
            <Dado rotulo="Para">{mensagem.destinatarios.join(', ') || '—'}</Dado>
            <Dado rotulo="Autenticação">
              <span className="flex flex-col gap-0.5">
                <SeloDeAutenticacao rotulo={autenticacao} />
                <span className="text-xs text-muted-foreground">{autenticacao.detalhe}</span>
              </span>
            </Dado>
            <Dado rotulo="Correlação">{mensagem.correlacao ?? '—'}</Dado>
          </dl>

          <div className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 text-sm">
            <p className="font-medium">Por que ficou retida</p>
            <p className="mt-0.5 text-muted-foreground">{mensagem.motivo}</p>
            {/*
              A causa raiz, dita em voz alta.
              O caso real que motivou tudo: a organização estava cadastrada SEM
              domínio, e por isso TODO e-mail dela caía aqui. Quem tria lia
              "remetente não pertence a nenhuma organização cadastrada" e
              concluía que a mensagem era o problema — quando o problema era o
              cadastro do cliente. Uma organização sem domínio é uma fábrica de
              retenções, e liberar uma a uma nunca esvazia a fila.
            */}
            {mensagem.dominio && !mensagem.organizacaoSugerida ? (
              <p className="mt-2 border-t border-warning/20 pt-2 text-muted-foreground">
                Nenhum cliente cadastrado tem o domínio{' '}
                <strong className="font-mono text-foreground">{mensagem.dominio}</strong>. Se este
                é um cliente seu, o cadastro dele está sem domínio — preencha em{' '}
                <strong className="text-foreground">Clientes</strong> e os próximos e-mails de{' '}
                <span className="font-mono">{mensagem.dominio}</span> abrem chamado sozinhos, sem
                passar por aqui.
              </p>
            ) : null}
          </div>

          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Conteúdo</p>
            {/*
              O HTML vem higienizado do servidor — a ingestão passa todo corpo
              por `prepareRichText`, com lista de permissão, antes de gravar. É
              por isso que a marcação é injetada em vez de escapada: quem tria
              precisa ler o e-mail como ele é, não como texto com tags à vista.
            */}
            <div
              className="max-h-64 overflow-y-auto rounded-lg border border-border bg-background px-4 py-3 text-sm leading-relaxed [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_li]:ml-4 [&_li]:list-disc [&_p]:mb-2 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: mensagem.corpoHtml }}
            />
          </div>

          <PainelDeDecisao mensagem={mensagem} aoDecidir={aoDecidir} />
        </div>
      ) : null}
    </Modal>
  );
}

function Dado({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{rotulo}</dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  );
}
